import { Component, ComponentProps, ComponentType } from 'react';
import { TValidatorSpec, TValidatorHash } from 'core/parseValidators';
import { withFormContext } from './FormContext';
import shallowEqual from './utils/shallowEqual';

export interface IPropsWithOwnDataAndErrorProps<D = any> {
  /** The data of the form component */
  data: D;
  /** Errors with the current data */
  errors: Record<string, string[]>;
  /** Name of the form component */
  name: string;
}

function propsWithOwnDataAndError<D = any>(props: IPropsWithOwnDataAndErrorProps<D>) {
  const { data, errors, name, ...restProps } = props;

  return {
    ...restProps,
    name,
    ownData: data[name as keyof D],
    ownError: errors[name],
  };
}

export interface IWithValidationComponent<D = any> extends IPropsWithOwnDataAndErrorProps<D> {
  /** The default value for the form component */
  defaultValue: string;
  /** The change function called on componentDidMount to set the default value */
  onChange(name: string, defaultValue: string): void;
  /** Registers the validators to the component when the component is mounted. */
  registerValidators(name: string, validators: TValidatorHash<D> | TValidatorSpec<D>[]): void;
  /** Removes validators on unmount */
  unregisterValidators(name: string): void;
  /** The validators to run on the data */
  validators: TValidatorHash<D> | TValidatorSpec<D>[];
}

/**
 * Adds support for inline definition of validators
 *
 * @deprecated deprecated since version 0.8.0 - migrate to withInputAPI instead
 *
 * @param {function} WrappedComponent
 */

export default function withValidation<TProps extends IWithValidationComponent>(
  WrappedComponent: ComponentType<TProps>
) {
  class Validated extends Component<ComponentProps<typeof WrappedComponent>> {
    componentDidMount() {
      const { defaultValue, name, onChange, registerValidators, validators = [] } = this.props;

      if (validators.length || Object.keys(validators).length) {
        registerValidators(name, validators);
      }

      if (defaultValue) {
        onChange(name, defaultValue);
      }
    }

    shouldComponentUpdate(nextProps: IWithValidationComponent) {
      const ownProps = propsWithOwnDataAndError(this.props);
      const ownNextProps = propsWithOwnDataAndError(nextProps);

      return !shallowEqual(ownProps, ownNextProps);
    }

    componentDidUpdate({
      validators: oldValidators,
    }: {
      validators: TValidatorHash | TValidatorSpec[];
    }) {
      const { name, registerValidators, validators } = this.props;

      if (
        oldValidators !== validators &&
        JSON.stringify(oldValidators) !== JSON.stringify(validators)
      ) {
        registerValidators(name, validators);
      }
    }

    componentWillUnmount() {
      const { name, unregisterValidators } = this.props;

      unregisterValidators(name);
    }

    render() {
      // eslint-disable-next-line react/destructuring-assignment, react/jsx-props-no-spreading
      return <WrappedComponent {...this.props} value={this.props.data[this.props.name]} />;
    }
  }

  return withFormContext(Validated);
}
